const costLayers = [
  {
    id: 'spread',
    number: '01',
    title: 'Spread',
    subtitle: 'The Entry Cost — Every Trade Starts in the Red',
    icon: 'ri-arrow-left-right-line',
    color: '#2563EB',
    accentLight: '#EEF4FF',
    accentBorder: '#BED9FF',
    description: [
      'The bid-ask gap is the most visible cost, but easy to underestimate. At 0.6 pips on EUR/USD, a single standard lot costs $6 just to open.',
      'A scalper doing 15 round-turns per day spends $90 daily — at 250 trading days, that\'s $22,500 a year on spread alone, with what looks like a "tight" rate.',
    ],
    statValue: '$22,500',
    statLabel: 'Annual spread cost for an active scalper at 0.6 pips',
    statSource: '15 RT × 250 days × $6/lot',
  },
  {
    id: 'commission',
    number: '02',
    title: 'Commission',
    subtitle: 'The Separate Fee — Spread Alone Tells Half the Story',
    icon: 'ri-bank-card-line',
    color: '#00A6B4',
    accentLight: '#E6FAFC',
    accentBorder: '#B8E8EC',
    description: [
      'On ECN accounts, commission is charged per side. A $3.50/side rate means $7 per round-turn on top of spread.',
      'A 0.2 pip spread with $7 commission costs more than a 1.2 pip spread with zero commission — but most traders only compare the spread number. Active traders can pay 2–3× more in commission than spread.',
    ],
    statValue: '2–3×',
    statLabel: 'Commission can exceed spread cost for active ECN traders',
    statSource: '$7 RT commission vs $2 spread on 0.2 pip ECN',
  },
  {
    id: 'swap',
    number: '03',
    title: 'Swap / Rollover',
    subtitle: 'The Holding Cost — Time Is Not Free When Leveraged',
    icon: 'ri-moon-clear-line',
    color: '#F59E0B',
    accentLight: '#FFF7E6',
    accentBorder: '#F5D89C',
    description: [
      'Overnight financing charges accumulate quietly. A GBP/JPY position held 10 days at 0.8 pips/day swap accumulates 8 pips — often more than the original spread.',
      'For swing traders holding multiple positions across weeks, swap can become the single largest cost category, silently turning winning trades into losers.',
    ],
    statValue: '4.4×',
    statLabel: 'Swap vs spread for a 10-day GBP/JPY position',
    statSource: '8 pips swap vs 1.8 pips spread',
  },
  {
    id: 'slippage',
    number: '04',
    title: 'Slippage',
    subtitle: 'The Execution Gap — The Price You Click vs The Price You Get',
    icon: 'ri-speed-up-line',
    color: '#7C3AED',
    accentLight: '#F4EBFE',
    accentBorder: '#D4BFF8',
    description: [
      'During news events, market orders can slip 2–5 pips. One NFP entry with 3 pip slippage costs $30 on a single lot — more than an entire day of spreads for many traders.',
      'Limit orders eliminate slippage risk but may not fill. The trade-off between certainty of execution and certainty of price is a hidden cost every active trader faces.',
    ],
    statValue: '$440/mo',
    statLabel: 'One slipped trade per day at 2 pips on GBP/USD',
    statSource: '22 trading days × $20 slippage per event',
  },
  {
    id: 'hidden',
    number: '05',
    title: 'Hidden Charges',
    subtitle: 'The Invisible Layer — Costs Not Printed on the Trade Ticket',
    icon: 'ri-eye-off-line',
    color: '#475569',
    accentLight: '#F1F4F8',
    accentBorder: '#D1D8E0',
    description: [
      'Currency conversion markups (0.3–0.5%), withdrawal fees ($20–40/wire), inactivity penalties, and spread widening during rollover all chip away at returns without appearing as line items.',
      'A EUR account trading USD/JPY might lose 0.5% per round-turn just in conversion — invisible but very real. There are at least 5 separate hidden cost categories to check with your broker.',
    ],
    statValue: '5+',
    statLabel: 'Separate hidden cost categories to check with your broker',
    statSource: 'Conversion, withdrawal, inactivity, rollover widening, data fees',
  },
];

export default function CostDeepDiveSection() {
  return (
    <section className="relative w-full bg-background-50 overflow-hidden">
      {/* Subtle top gradient line */}
      <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-transparent via-[#E6EAF0] to-transparent"></div>

      <div className="w-full max-w-main mx-auto px-4 md:px-6 py-[64px] md:py-[88px]">
        {/* Asymmetric header: label on left, title spans full width */}
        <div className="flex flex-col md:flex-row md:items-end gap-4 md:gap-10 mb-12 md:mb-16">
          <div className="flex-1">
            <h2 className="text-[28px] md:text-[38px] font-[750] leading-[36px] md:leading-[46px] text-[#101828] tracking-[-0.015em] max-w-[640px]">
              Five forces that shape every trade, from entry to exit.
            </h2>
          </div>
          <p className="hidden md:block text-[15px] leading-[24px] text-[#667085] max-w-[260px] flex-shrink-0 text-right">
            Not just definitions — real numbers from real trading conditions.
          </p>
        </div>

        {/* Visual infographic: big number + content alternating layout */}
        <div className="flex flex-col">
          {costLayers.map((layer, idx) => {
            const isEven = idx % 2 === 0;
            return (
              <div
                key={layer.id}
                className={`group relative flex flex-col ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'} gap-0 ${idx < costLayers.length - 1 ? 'pb-16 md:pb-20' : ''}`}
              >
                {/* Connector line between items */}
                {idx < costLayers.length - 1 && (
                  <div className="hidden md:block absolute left-[calc(50%-1px)] top-0 bottom-0 w-[2px] bg-background-200 transition-colors duration-500"
                       style={isEven ? { left: 'calc(50% - 1px)' } : { left: 'calc(50% - 1px)' }}>
                  </div>
                )}

                {/* Left half (or right if reversed) — Big Number + Icon */}
                <div className={`flex-1 flex ${isEven ? 'md:justify-end md:pr-16' : 'md:justify-start md:pl-16'} items-start pt-2`}>
                  <div className="flex flex-col items-start gap-3">
                    {/* Big number */}
                    <span className="text-[72px] md:text-[96px] font-[800] leading-none tracking-[-0.04em] text-foreground-100 select-none transition-all duration-500 group-hover:scale-[1.04] group-hover:text-foreground-200 origin-center">
                      {layer.number}
                    </span>
                    {/* Mobile title (hidden on desktop) */}
                    <div className="md:hidden flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
                        style={{ backgroundColor: layer.accentLight }}
                      >
                        <i className={`${layer.icon} text-lg`} style={{ color: layer.color }}></i>
                      </div>
                      <h3 className="text-[22px] font-[750] text-[#101828]">{layer.title}</h3>
                    </div>
                  </div>
                </div>

                {/* Right half (or left if reversed) — Content Card */}
                <div className="flex-1">
                  {/* Desktop title */}
                  <div className="hidden md:flex items-center gap-3 mb-4">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform duration-300 group-hover:scale-110"
                      style={{ backgroundColor: layer.accentLight }}
                    >
                      <i className={`${layer.icon} text-xl`} style={{ color: layer.color }}></i>
                    </div>
                    <div>
                      <h3 className="text-[24px] font-[750] leading-[30px] text-[#101828]">{layer.title}</h3>
                      <p className="text-[13px] font-semibold text-[#667085] mt-0.5">{layer.subtitle}</p>
                    </div>
                  </div>

                  {/* Mobile subtitle */}
                  <p className="md:hidden text-[13px] font-semibold text-[#667085] mt-1 mb-3">{layer.subtitle}</p>

                  {/* Description — split into short paragraphs */}
                  <div className="mb-6 flex flex-col gap-3">
                    {layer.description.map((para, pi) => (
                      <p key={pi} className="text-[15px] leading-[26px] text-[#475467]">
                        {para}
                      </p>
                    ))}
                  </div>

                  {/* Stat callout */}
                  <div
                    className="flex items-stretch rounded-2xl overflow-hidden border transition-all duration-300 group-hover:-translate-y-[2px]"
                    style={{ borderColor: layer.accentBorder, backgroundColor: layer.accentLight }}
                  >
                    <div
                      className="flex items-center justify-center px-5 md:px-6 py-4 flex-shrink-0"
                      style={{ backgroundColor: `${layer.color}14` }}
                    >
                      <span className="text-[28px] md:text-[34px] font-[800] tracking-[-0.02em]" style={{ color: layer.color }}>
                        {layer.statValue}
                      </span>
                    </div>
                    <div className="flex flex-col justify-center px-4 py-3">
                      <span className="text-[14px] font-semibold text-[#101828] leading-[20px]">{layer.statLabel}</span>
                      <span className="text-[12px] text-[#667085] mt-0.5">{layer.statSource}</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom contextual divider */}
        <div className="mt-16 pt-8 border-t border-background-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
          <p className="text-[13px] leading-[20px] text-[#94A3B8] max-w-[520px]">
            These examples use typical retail trading conditions. Actual costs vary by broker, account type, trade size, and market conditions.
          </p>
          <span className="text-[12px] font-semibold text-[#94A3B8] whitespace-nowrap">Educational guide only</span>
        </div>
      </div>
    </section>
  );
}